export const LESSON_XP = 10;
export const BLANK_XP = 5;
export const LEVEL_XP = 50;
export const STREAK_FREEZES = 2;
export const DAILY_CHALLENGE = 3;
export const STREAK_GOALS = [7, 14, 30, 50] as const;
export const APP_VERSION = '1.0.0';

export type StreakGoal = (typeof STREAK_GOALS)[number];

export interface LessonFinish {
  lessonId: string;
  date: string;
  correctBlanks: number;
  skipped: boolean;
}

export interface StudyStats {
  finishes: LessonFinish[];
  current: number;
  longest: number;
  lastDate: string | null;
  freezesLeft: number;
  studyDates: string[];
  freezeDates: string[];
  streakGoal: StreakGoal | null;
  reminderTime: string | null;
  notificationsOn: boolean;
  freezePromptSeen: boolean;
}

export interface XpBreakdown {
  lesson: number;
  blanks: number;
  total: number;
}

export interface WeekDot {
  date: string;
  label: string;
  state: 'studied' | 'freeze' | 'empty';
}

const WEEK_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

export function emptyStudyStats(): StudyStats {
  return {
    finishes: [],
    current: 0,
    longest: 0,
    lastDate: null,
    freezesLeft: STREAK_FREEZES,
    studyDates: [],
    freezeDates: [],
    streakGoal: null,
    reminderTime: null,
    notificationsOn: false,
    freezePromptSeen: false,
  };
}

export function isStreakGoal(value: number): value is StreakGoal {
  return (STREAK_GOALS as readonly number[]).includes(value);
}

export function localDateKey(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export function levelFromXp(xp: number): number {
  const safe = Number.isFinite(xp) ? Math.max(0, Math.floor(xp)) : 0;
  return 1 + Math.floor(safe / LEVEL_XP);
}

export function xpBreakdown(correctBlanks: number, skipped: boolean): XpBreakdown {
  const blanks = skipped ? 0 : Math.max(0, Math.floor(correctBlanks) || 0) * BLANK_XP;
  return { lesson: LESSON_XP, blanks, total: LESSON_XP + blanks };
}

/** Lesson XP once per id, plus the best recap bonus for that id. Other completed ids count as 10. */
export function totalXp(stats: Pick<StudyStats, 'finishes'>, completedIds: string[]): number {
  const blanks = new Map<string, number>();
  for (const finish of stats.finishes) {
    if (!finish.lessonId) continue;
    const count = Math.max(0, Math.floor(finish.correctBlanks) || 0);
    blanks.set(finish.lessonId, Math.max(blanks.get(finish.lessonId) || 0, count));
  }
  const ids = new Set(completedIds.filter(Boolean));
  for (const id of blanks.keys()) ids.add(id);
  let xp = 0;
  for (const id of ids) xp += LESSON_XP + (blanks.get(id) || 0) * BLANK_XP;
  return xp;
}

export function daysBetween(earlier: string, later: string): number {
  const [y1, m1, d1] = earlier.split('-').map(Number);
  const [y2, m2, d2] = later.split('-').map(Number);
  const start = Date.UTC(y1, (m1 || 1) - 1, d1 || 1);
  const end = Date.UTC(y2, (m2 || 1) - 1, d2 || 1);
  return Math.round((end - start) / 86400000);
}

export function shiftDate(day: string, delta: number): string {
  const [year, month, date] = day.split('-').map(Number);
  const value = new Date(Date.UTC(year, (month || 1) - 1, date || 1));
  value.setUTCDate(value.getUTCDate() + delta);
  const nextMonth = String(value.getUTCMonth() + 1).padStart(2, '0');
  const nextDay = String(value.getUTCDate()).padStart(2, '0');
  return `${value.getUTCFullYear()}-${nextMonth}-${nextDay}`;
}

function missedDates(lastDate: string, today: string): string[] {
  const gap = daysBetween(lastDate, today) - 1;
  const dates: string[] = [];
  for (let offset = 1; offset <= gap; offset += 1) dates.push(shiftDate(lastDate, offset));
  return dates;
}

function withStudyDay(stats: StudyStats, today: string): StudyStats {
  const studyDates = stats.studyDates.includes(today) ? stats.studyDates : [...stats.studyDates, today];
  return {
    ...stats,
    studyDates: [...studyDates].sort(),
    freezeDates: stats.freezeDates.filter((day) => day !== today).sort(),
    lastDate: today,
  };
}

/**
 * Freeze-aware lesson streak. A gap larger than the freezes left resets the current
 * streak to 1 and does not spend a freeze. nextStreak() is unchanged for practice.
 */
export function advanceStreak(stats: StudyStats, today: string): StudyStats {
  const freezesLeft = Math.max(0, Math.min(STREAK_FREEZES, Math.floor(stats.freezesLeft) || 0));
  const current = Math.max(0, Math.floor(stats.current) || 0);
  const longest = Math.max(current, Math.floor(stats.longest) || 0);
  const base = { ...stats, freezesLeft, current, longest };

  if (!base.lastDate) {
    const next = withStudyDay({ ...base, current: 1, longest: Math.max(longest, 1) }, today);
    return next;
  }
  if (base.lastDate === today) return withStudyDay(base, today);

  const apart = daysBetween(base.lastDate, today);
  if (apart < 0) return withStudyDay(base, today);

  const gap = apart - 1;
  if (gap === 0) {
    const nextCurrent = current + 1;
    return withStudyDay({ ...base, current: nextCurrent, longest: Math.max(longest, nextCurrent) }, today);
  }
  if (gap <= freezesLeft) {
    const missed = missedDates(base.lastDate, today);
    const freezeDates = new Set(base.freezeDates);
    const studied = new Set(base.studyDates);
    for (const day of missed) {
      if (!studied.has(day)) freezeDates.add(day);
    }
    const nextCurrent = current + 1;
    return withStudyDay({
      ...base,
      current: nextCurrent,
      longest: Math.max(longest, nextCurrent),
      freezesLeft: freezesLeft - gap,
      freezeDates: [...freezeDates].sort(),
    }, today);
  }
  return withStudyDay({ ...base, current: 1, longest: Math.max(longest, 1) }, today);
}

export function applyFinish(
  stats: StudyStats,
  input: { lessonId: string; date: string; correctBlanks: number; skipped: boolean },
): { stats: StudyStats; added: number; already: boolean } {
  const correct = input.skipped ? 0 : Math.max(0, Math.floor(input.correctBlanks) || 0);
  const existing = stats.finishes.find((finish) => finish.lessonId === input.lessonId);
  const already = Boolean(existing);
  const kept = existing ? Math.max(existing.correctBlanks, correct) : correct;
  const finishes = existing
    ? stats.finishes.map((finish) => (
      finish.lessonId === input.lessonId
        ? { ...finish, correctBlanks: kept, skipped: finish.skipped && input.skipped }
        : finish
    ))
    : [...stats.finishes, { lessonId: input.lessonId, date: input.date, correctBlanks: correct, skipped: input.skipped }];
  const advanced = advanceStreak({ ...stats, finishes }, input.date);
  const added = already ? Math.max(0, kept - (existing?.correctBlanks || 0)) * BLANK_XP : LESSON_XP + correct * BLANK_XP;
  return { stats: advanced, added, already };
}

export function weekDots(today: string, studyDates: string[], freezeDates: string[]): WeekDot[] {
  const [year, month, date] = today.split('-').map(Number);
  const anchor = new Date(Date.UTC(year, (month || 1) - 1, date || 1));
  const sunday = shiftDate(today, -anchor.getUTCDay());
  const studied = new Set(studyDates);
  const frozen = new Set(freezeDates);
  return WEEK_LABELS.map((label, index) => {
    const day = shiftDate(sunday, index);
    const state = studied.has(day) ? 'studied' : frozen.has(day) ? 'freeze' : 'empty';
    return { date: day, label, state };
  });
}

/** Floating local daily event. No timezone suffix, so the phone keeps the chosen clock time. */
export function reminderIcs(time: string, today: string): string | null {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(time);
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(today);
  if (!match || !day) return null;
  const stamp = `${day[1]}${day[2]}${day[3]}T${match[1]}${match[2]}00`;
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//My New Start//Reminder//EN',
    'BEGIN:VEVENT',
    `DTSTART:${stamp}`,
    'RRULE:FREQ=DAILY',
    'SUMMARY:My New Start',
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
}

export function dailyChallengeProgress(
  finishes: Pick<LessonFinish, 'lessonId' | 'date'>[],
  today: string,
): { done: number; goal: number } {
  const ids = new Set(finishes.filter((finish) => finish.date === today && finish.lessonId).map((finish) => finish.lessonId));
  return { done: ids.size, goal: DAILY_CHALLENGE };
}

export function sortSavedCards<T extends { title: string; savedAt: string }>(cards: T[], mode: 'title' | 'date'): T[] {
  const copy = [...cards];
  if (mode === 'title') {
    copy.sort((a, b) => a.title.localeCompare(b.title, 'en', { sensitivity: 'base' }) || a.savedAt.localeCompare(b.savedAt));
    return copy;
  }
  copy.sort((a, b) => {
    if (!a.savedAt && !b.savedAt) return a.title.localeCompare(b.title, 'en', { sensitivity: 'base' });
    if (!a.savedAt) return 1;
    if (!b.savedAt) return -1;
    return b.savedAt.localeCompare(a.savedAt);
  });
  return copy;
}

export function planLabel(input: {
  hasPremiumAccess: boolean;
  premiumTier: string | null;
  subscriptionStatus: string | null;
}): string {
  if (!input.hasPremiumAccess) return 'Free';
  if (input.premiumTier === 'guest_pass') return 'Guest pass';
  if (input.premiumTier) return `Premium (${input.premiumTier})`;
  if (input.subscriptionStatus === 'active') return 'Premium';
  return 'Premium';
}
